import { Button, CircularProgress, Grid, InputAdornment, Paper, Typography } from '@mui/material';
import { mixed, number, object, string } from 'yup';
import React from 'react';
import { Form, Formik } from 'formik';
import InputForm from '../../commun/input-form';
import { Link } from 'react-router-dom';
import './direct-debit.form.scss';
import InputIbanForm from '../../commun/input-iban-form';
import { ERROR_MAIL } from '../../../utils/constants.utils';

/**
 * Composant la page prélévement automatique
 * @constructor
 */
export default function DirectDebitForm(props: {
    handleSumbitForm: (values: any) => void;
    handleChangeFile: (values: any) => void;
    sendForm: boolean;
    contentType: string;
    fileName: string;
    isMensualise: boolean;
}) {
    //Ajout des ocnstantes
    const message = 'Champ obligatoire';
    const eleven = 11;
    const eight = 8;

    const nbCaracteresTel = 9;

    // Schéma de validation des données
    // champs :
    // holder, iban, bic, members, rib
    const validationSchema = object({
        holder: string().required(message),
        iban: string()
            .required(message)
            .test('valid-iban', "L'iban n'a pas le bon format", value => {
                if (!value) {
                    return false;
                }

                const lengthIban = 27;
                const ibanClean = value.replace(/\s/g, '').toUpperCase();
                if (ibanClean.length !== lengthIban) {
                    return false;
                }

                const ibanWith4firstAtEnd = ibanClean.substring(4) + ibanClean.substring(0, 4);
                const convertIban = ibanWith4firstAtEnd.replace(/[A-Z]/g, match => String(match.charCodeAt(0) - 55));

                try {
                    const remainder = BigInt(convertIban) % 97n;

                    return remainder === 1n;
                } catch (e) {
                    return false;
                }
            }),
        bic: string()
            .required(message)
            .max(eleven, 'Le champ Bic peut avoir que 11 caratères au maximum')
            .min(eight, 'Le champ Bic doit avoir 8 caratères au minimum'),
        members: number()
            .integer('Le nombre doit être un nombre entier.')
            .positive('Le nombre doit être supérieur à 0.')
            .required(message),
        rib: mixed().test('rib-not-good-format', "Le fichier n'est pas au bon format", () => {
            if (props.contentType) {
                const isMultipleExtension = /\.\w+\.\w+$/.test(props.fileName);
                if (props.contentType === 'application/pdf' && !isMultipleExtension) {
                    return true;
                }
            }
            return false;
        }),
        email: string()
            .email(ERROR_MAIL)
            .required("L'adresse email est obligatoire")
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
        tel: string()
            .matches(/^[67]\d+$/, {
                message: 'Le numéro doit commencer par 6 ou 7 et ne contenir que des chiffres.',
            })
            .required('Le numéro de téléphone est obligatoire')
            .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
    });

    // Valeurs par défaut
    const iniValues = {
        holder: '',
        iban: '',
        bic: '',
        members: props.isMensualise ? 1 : '',
        rib: '',
        email: '',
        tel: '',
    };

    // Création de la page
    return (
        // fragment
        <>
            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    {/*titre*/}
                    <Typography variant="h5">
                        {props.isMensualise ? 'Changement de coordonnées bancaires' : 'Prélévement mensuel'}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={6} sx={{ textAlign: 'right' }}>
                    {/*explication * */}
                    <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                </Grid>
            </Grid>
            {/*Paper*/}
            <Paper elevation={3} className="paperForm-directDebit">
                {/*//Init Formulaire*/}
                <Formik initialValues={iniValues} onSubmit={props.handleSumbitForm} validationSchema={validationSchema}>
                    {/*formulaire*/}
                    <Form>
                        <Grid container spacing={2}>
                            {/*titulaire du compte*/}
                            <Grid item xs={12} md={6}>
                                <InputForm required id="holder" name="holder" label="Titulaire" />
                            </Grid>
                            {/*Nombre de personnes dans le foyé*/}
                            {!props.isMensualise ? (
                                <Grid item xs={12} md={6}>
                                    <InputForm
                                        required
                                        type="number"
                                        id="members"
                                        name="members"
                                        label="Nombre de personnes dans le foyer"
                                        min="1"
                                        step="1"
                                    />
                                </Grid>
                            ) : null}
                            <Grid item xs={12} md={6}>
                                <InputForm required id="email" name="email" type="email" label="Adresse e-mail" />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <InputForm
                                    required
                                    id="tel"
                                    name="tel"
                                    type="tel"
                                    label="Numéro de téléphone"
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">0</InputAdornment>,
                                    }}
                                    autoComplete={'tel-national'}
                                />
                            </Grid>
                            {/*iban*/}
                            <Grid item xs={12} md={6}>
                                <InputIbanForm required id="iban" name="iban" label="Iban" />
                            </Grid>
                            {/*bic*/}
                            <Grid item xs={12} md={6}>
                                <InputForm required id="bic" name="bic" label="Bic" />
                            </Grid>
                            {/*Document RIB*/}
                            <Grid item xs={12} md={6}>
                                <InputForm
                                    required
                                    type="file"
                                    id="rib"
                                    name="rib"
                                    label="RIB (.pdf)"
                                    accept="application/pdf"
                                    handleChange={props.handleChangeFile}
                                />
                                {!props.isMensualise ? (
                                    <Typography variant={'body1'} sx={{ color: '#d32f2f' }}>
                                        La demande sera effective pour l’année N+1
                                    </Typography>
                                ) : null}
                            </Grid>
                            <Grid item xs={12} md={6}></Grid>

                            <Grid item xs={0} sm={9} />
                            <Grid item xs={12} sm={3}>
                                <Typography variant={'button'} className={'buttons-form'}>
                                    {/*bouton annulé*/}
                                    <Button variant="neutral" component={Link} to={'/'}>
                                        Annuler
                                    </Button>
                                    {/*bouton valider*/}
                                    <Button type="submit" variant="contained" disabled={!props.sendForm}>
                                        {!props.sendForm ? <CircularProgress size={26} color="inherit" /> : 'VALIDER'}
                                    </Button>
                                </Typography>
                            </Grid>
                        </Grid>
                    </Form>
                </Formik>
            </Paper>
        </>
    );
}
